import { decrypt, encrypt } from './crypto';

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);
const MAX_DECRYPT_DEPTH = 6;

function isFormData(value) {
  return typeof FormData !== 'undefined' && value instanceof FormData;
}

function isLikelyBase64Cipher(value) {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  return trimmed.length >= 24 && /^[A-Za-z0-9+/]+=*$/.test(trimmed);
}

function parseDecryptedJson(decrypted) {
  if (!decrypted) return null;
  try {
    return JSON.parse(decrypted);
  } catch {
    return decrypted;
  }
}

function tryDecryptCipher(cipher, depth = 0) {
  if (!isLikelyBase64Cipher(cipher) || depth > MAX_DECRYPT_DEPTH) return null;
  const plain = decrypt(String(cipher).trim());
  if (!plain) return null;

  let parsed = parseDecryptedJson(plain);
  if (typeof parsed === 'string' && isLikelyBase64Cipher(parsed)) {
    const nested = tryDecryptCipher(parsed, depth + 1);
    if (nested != null) parsed = nested;
  }
  return parsed;
}

function syncPayloadAndData(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;

  const payload = obj.payload ?? obj.Payload;
  const data = obj.data ?? obj.Data;
  const payloadObj =
    payload != null && typeof payload === 'object' && !Array.isArray(payload) ? payload : null;
  const dataObj = data != null && typeof data === 'object' && !Array.isArray(data) ? data : null;

  if (payloadObj && !dataObj) {
    return { ...obj, data: payloadObj, payload: payloadObj };
  }
  if (dataObj && !payloadObj) {
    return { ...obj, payload: dataObj, data: dataObj };
  }
  return obj;
}

function deepDecryptValue(value, depth = 0) {
  if (value == null || depth > MAX_DECRYPT_DEPTH) return value;

  if (typeof value === 'string') {
    const parsed = tryDecryptCipher(value, depth);
    if (parsed == null) return value;
    if (typeof parsed === 'object') return deepDecryptValue(parsed, depth + 1);
    return parsed;
  }

  if (Array.isArray(value)) {
    return value.map((item) => deepDecryptValue(item, depth + 1));
  }

  if (typeof value === 'object') {
    const out = {};
    for (const [key, entry] of Object.entries(value)) {
      out[key] = deepDecryptValue(entry, depth + 1);
    }
    return syncPayloadAndData(out);
  }

  return value;
}

function isFileValue(value) {
  return (
    (typeof File !== 'undefined' && value instanceof File) ||
    (typeof Blob !== 'undefined' && value instanceof Blob)
  );
}

function formDataToPlainObject(formData) {
  const obj = {};
  formData.forEach((value, key) => {
    if (key === 'data' || key === 'payload') return;
    if (isFileValue(value)) return;
    if (Object.hasOwn(obj, key)) {
      const existing = obj[key];
      obj[key] = Array.isArray(existing) ? [...existing, value] : [existing, value];
    } else {
      obj[key] = value;
    }
  });
  return obj;
}

/** Multipart: encrypt all non-file fields into `data`; keep file parts unchanged. */
export function encryptFormDataPayload(formData) {
  if (!isFormData(formData)) return formData;

  let hasPlainScalars = false;
  let existingCipher = null;

  formData.forEach((value, key) => {
    if ((key === 'data' || key === 'payload') && typeof value === 'string' && isLikelyBase64Cipher(value)) {
      existingCipher = value;
      return;
    }
    if (!isFileValue(value)) hasPlainScalars = true;
  });

  if (existingCipher && !hasPlainScalars) return formData;

  const plain = formDataToPlainObject(formData);
  const encrypted = encrypt(JSON.stringify(plain));
  const fd = new FormData();
  fd.append('data', encrypted);

  formData.forEach((value, key) => {
    if (!isFileValue(value)) return;
    if (typeof value.name === 'string' && value.name) {
      fd.append(key, value, value.name);
    } else {
      fd.append(key, value);
    }
  });

  return fd;
}

function isUrlSearchParams(value) {
  return typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams;
}

function urlSearchParamsToObject(params) {
  const obj = {};
  params.forEach((value, key) => {
    if (Object.hasOwn(obj, key)) {
      const existing = obj[key];
      obj[key] = Array.isArray(existing) ? [...existing, value] : [existing, value];
    } else {
      obj[key] = value;
    }
  });
  return obj;
}

/** Serialize and AES-encrypt JSON request bodies. Backend expects raw ciphertext string body. */
export function encryptApiPayload(payload) {
  if (payload == null) return payload;
  if (isFormData(payload)) return encryptFormDataPayload(payload);
  // JSON.stringify(URLSearchParams) is "{}" — convert entries first so fields are not dropped.
  if (isUrlSearchParams(payload)) {
    return encrypt(JSON.stringify(urlSearchParamsToObject(payload)));
  }
  const json = typeof payload === 'string' ? payload : JSON.stringify(payload);
  return encrypt(json);
}

/** Decrypt backend payloads — walks the full tree so `data` / `payload` are never left encrypted. */
export function decryptApiPayload(responseData) {
  if (responseData == null) return responseData;
  return deepDecryptValue(responseData);
}

export function applyApiCryptoInterceptors(axiosInstance) {
  axiosInstance.interceptors.request.use((config) => {
    const method = (config.method || 'get').toLowerCase();
    if (!MUTATING_METHODS.has(method) || config.data == null) return config;
    if (config.skipApiEncryption) return config;
    // eslint-disable-next-line no-param-reassign
    config.data = encryptApiPayload(config.data);
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => {
      if (response?.data != null) {
        // eslint-disable-next-line no-param-reassign
        response.data = decryptApiPayload(response.data);
      }
      return response;
    },
    (error) => {
      if (error?.response?.data != null) {
        // eslint-disable-next-line no-param-reassign
        error.response.data = decryptApiPayload(error.response.data);
      }
      return Promise.reject(error);
    }
  );
}
